/**
 * Terms Acceptance Modal
 *
 * Displayed as a full-screen overlay when a user's accepted legal document versions
 * are behind the current platform versions. Cannot be dismissed without accepting.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, FileText, Shield, AlertCircle } from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { coreAPI, type LegalContent, type LegalAcceptRequest } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export default function TermsAcceptanceModal() {
  const { user, updateUser } = useAuth();
  const [legalContent, setLegalContent] = useState<LegalContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');

  // Determine what needs acceptance. The same resolved versions are posted back
  // on accept so the modal never asks for one version and records another.
  const currentTermsVersion = user?.terms_current_version ?? 1;
  const currentPrivacyVersion = user?.privacy_current_version ?? 1;
  const needsTermsAcceptance = !!user
    && (user.terms_accepted_version ?? 0) < currentTermsVersion;
  const needsPrivacyAcceptance = !!user
    && (user.privacy_accepted_version ?? 0) < currentPrivacyVersion;
  const needsAcceptance = needsTermsAcceptance || needsPrivacyAcceptance;

  const loadLegalContent = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    coreAPI.getLegalContent()
      .then((res) => setLegalContent(res.data))
      .catch(() => {
        // The modal can't be dismissed, so surface the failure inline with a
        // retry instead of leaving an un-actionable overlay on screen.
        setLoadError(true);
        toast.error('Failed to load legal documents');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!needsAcceptance) return;
    loadLegalContent();
  }, [needsAcceptance, loadLegalContent]);

  const handleAccept = useCallback(async () => {
    if (!user) return;

    setSubmitting(true);
    try {
      const payload: LegalAcceptRequest = {};
      if (needsTermsAcceptance) {
        payload.terms_version = currentTermsVersion;
      }
      if (needsPrivacyAcceptance) {
        payload.privacy_version = currentPrivacyVersion;
      }

      const { data } = await coreAPI.acceptLegal(payload);

      // Apply the versions the backend actually recorded. checkAuthStatus()
      // skips its network call while impersonating, so relying on it here left
      // the modal up permanently in a "Login As" session.
      updateUser({
        terms_accepted_version: data.terms_accepted_version,
        privacy_accepted_version: data.privacy_accepted_version,
      });

      toast.success('Legal documents accepted');
    } catch (error) {
      console.error('Failed to accept legal documents:', error);
      toast.error('Failed to accept. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [
    user, needsTermsAcceptance, needsPrivacyAcceptance,
    currentTermsVersion, currentPrivacyVersion, updateUser,
  ]);

  // Don't render if no acceptance needed
  if (!needsAcceptance) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            Updated Legal Documents
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Please review and accept the updated documents to continue using the platform.
          </p>
        </div>

        {/* Tab Selector */}
        {needsTermsAcceptance && needsPrivacyAcceptance && (
          <div className="px-6 pt-3 flex-shrink-0">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('terms')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'terms'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FileText className="h-4 w-4" />
                Terms & Conditions
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('privacy')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'privacy'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Shield className="h-4 w-4" />
                Privacy Policy
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : legalContent ? (
            <div className="prose prose-sm prose-gray max-w-none">
              {(activeTab === 'terms' && needsTermsAcceptance) ? (
                legalContent.terms_content ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(legalContent.terms_content) }} />
                ) : (
                  <p className="text-gray-500 italic">
                    Terms & Conditions content has been updated. Please accept to continue.
                  </p>
                )
              ) : (
                legalContent.privacy_content ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(legalContent.privacy_content) }} />
                ) : (
                  <p className="text-gray-500 italic">
                    Privacy Policy content has been updated. Please accept to continue.
                  </p>
                )
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <p className="text-gray-700">
                The legal documents could not be loaded, so they can&apos;t be accepted
                yet.
              </p>
              <Button variant="outline" onClick={loadLegalContent}>
                Try again
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex-shrink-0 space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="accept-legal"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              disabled={loading || loadError}
              className="mt-0.5"
            />
            <label htmlFor="accept-legal" className="text-sm text-gray-700 cursor-pointer select-none">
              I have read and accept the{' '}
              {needsTermsAcceptance && 'Terms & Conditions'}
              {needsTermsAcceptance && needsPrivacyAcceptance && ' and '}
              {needsPrivacyAcceptance && 'Privacy Policy'}
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleAccept}
              disabled={!accepted || submitting || loading || loadError}
              size="lg"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
